/**
 * Supervisor Assessment Page
 *
 * PKKS (Penilaian Kinerja Kepala Sekolah) rubric-based assessment:
 * - 40 criteria across 9 competency areas
 * - 4-level rubric scoring (Excellent/Very Good/Good/Needs Improvement)
 * - Evidence collection methods (Document Review, Observation, Interview)
 * - Progress tracking with milestones
 * - Auto-save functionality
 * - School selection for multi-school supervisors
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentService, rubricService, schoolService } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useAutoSave, useLoadSavedData, useBackgroundSync } from '../hooks';
import SchoolSelector from '../components/SchoolSelector';

// Dialog Component
const Dialog = ({ isOpen, onClose, type = 'info', title, message, confirmText, cancelText, onConfirm }) => {
  if (!isOpen) return null;

  const icons = {
    success: (
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
        <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    ),
    error: (
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
        <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    ),
    warning: (
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
        <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
    ),
    info: (
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
        <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    ),
    confirm: (
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-purple-100 mb-4">
        <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    )
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Dialog */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 transform transition-all">
          {icons[type]}

          {title && (
            <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
              {title}
            </h3>
          )}

          <p className="text-gray-600 text-center mb-6 whitespace-pre-line">
            {message}
          </p>

          <div className="flex gap-3 justify-center">
            {cancelText && (
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                {cancelText}
              </button>
            )}
            <button
              onClick={() => {
                if (onConfirm) onConfirm();
                onClose();
              }}
              className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                type === 'error'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : type === 'success'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : type === 'warning'
                  ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {confirmText || 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SupervisorAssessment = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();

  // Refs
  const questionScrollRef = useRef(null);
  const rubricLoadedRef = useRef(false);

  // State
  const [rubric, setRubric] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');
  const [expandedEvidence, setExpandedEvidence] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});

  // School selection state
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showSchoolSelector, setShowSchoolSelector] = useState(true);

  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: '',
    onConfirm: null
  });

  const showDialog = (options) => {
    setDialog({ isOpen: true, ...options });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Storage key for this assessment
  const storageKey = `pkks_supervisor_assessment_${profile?.id}`;

  // Load saved data from localStorage
  const { savedData, clearSavedData } = useLoadSavedData(storageKey);

  // Auto-save to localStorage (500ms debounce)
  const { isSaving, lastSaved } = useAutoSave(storageKey, {
    responses,
    currentIndex,
    sessionId,
  }, 500);

  // Background sync to database (5 minutes)
  const { isSyncing } = useBackgroundSync(
    storageKey,
    async (data) => {
      if (sessionId) {
        await assessmentService.autoSave(sessionId, data);
      }
    },
    300000
  );

  // PKKS Rating scale (4-level rubric)
  const ratingLevels = [
    {
      value: 'excellent',
      numericValue: 4,
      label: 'Sangat Baik (4)',
      color: 'bg-green-50 hover:bg-green-100 border-green-300',
      selectedColor: 'border-green-500 bg-green-50',
      shortLabel: 'SB'
    },
    {
      value: 'very_good',
      numericValue: 3,
      label: 'Baik (3)',
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-300',
      selectedColor: 'border-blue-500 bg-blue-50',
      shortLabel: 'B'
    },
    {
      value: 'good',
      numericValue: 2,
      label: 'Cukup (2)',
      color: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-300',
      selectedColor: 'border-yellow-500 bg-yellow-50',
      shortLabel: 'C'
    },
    {
      value: 'satisfactory',
      numericValue: 1,
      label: 'Perlu Peningkatan (1)',
      color: 'bg-red-50 hover:bg-red-100 border-red-300',
      selectedColor: 'border-red-500 bg-red-50',
      shortLabel: 'PP'
    }
  ];

  // Evidence methods labels
  const evidenceMethodLabels = {
    telaah_dokumen: 'Telaah Dokumen',
    observasi: 'Observasi',
    wawancara: 'Wawancara'
  };

  // Load rubric data when school is selected
  useEffect(() => {
    if (selectedSchoolId && !rubricLoadedRef.current) {
      rubricLoadedRef.current = true;
      loadRubricData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  // Restore saved progress
  useEffect(() => {
    if (savedData && savedData.responses) {
      // Check if this is valid saved data (not from a completed session)
      if (savedData.sessionId) {
        // Verify the session is still in progress before restoring
        assessmentService.getSession(savedData.sessionId).then(({ session }) => {
          if (session && session.status !== 'completed') {
            setResponses(savedData.responses);
            setCurrentIndex(savedData.currentIndex || 0);
            setSessionId(savedData.sessionId);
          } else {
            // Session completed or not found, clear old data
            console.log('Clearing old session data');
            clearSavedData();
          }
        });
      } else {
        setResponses(savedData.responses);
        setCurrentIndex(savedData.currentIndex || 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedData]);

  const loadRubricData = async () => {
    try {
      // Load PKKS rubric with all criteria and rating levels
      const result = await rubricService.getFullRubric('supervisor');

      const { rubric: rubricData, criteria: criteriaData, error } = result;

      if (error) {
        console.error('Rubric service error:', error);
        throw error;
      }

      if (!rubricData) {
        throw new Error('No active PKKS rubric found for supervisors');
      }

      if (!criteriaData || criteriaData.length === 0) {
        throw new Error('No criteria found for the rubric');
      }

      setRubric(rubricData);
      setCriteria(criteriaData);

      // Create session if not exists
      if (!sessionId && !savedData?.sessionId) {
        const { session, error: sessionError } = await assessmentService.createSession({
          user_id: profile?.id,
          total_questions: criteriaData.length,
          is_anonymous: false,
          target_school_id: selectedSchoolId, // Target school for this assessment
          auto_save_data: { role: 'supervisor', rubric_id: rubricData.id }
        });

        if (!sessionError && session) {
          setSessionId(session.id);
        }
      }
    } catch (error) {
      console.error('Error loading rubric:', error);
      // Don't show alert, let the error state handle it
    } finally {
      setLoading(false);
    }
  };

  const handleRating = async (level, evidenceMethods = []) => {
    const criterion = criteria[currentIndex];
    const levelInfo = ratingLevels.find(l => l.value === level);

    const newResponses = {
      ...responses,
      [criterion.id]: {
        criterion_id: criterion.id,
        rubric_id: rubric.id,
        rating_level: level,
        rating_score: levelInfo.numericValue,
        evidence_methods_used: evidenceMethods,
        criterion_code: criterion.criterion_code,
        criterion_name: criterion.criterion_name
      }
    };

    setResponses(newResponses);

    // Auto-advance to next question after brief delay
    setTimeout(() => {
      if (currentIndex < criteria.length - 1) {
        handleNext();
      }
    }, 300);
  };

  const handleEvidenceToggle = (method) => {
    const criterion = criteria[currentIndex];
    const currentResponse = responses[criterion.id] || { evidence_methods_used: [] };
    const currentMethods = currentResponse.evidence_methods_used || [];

    let newMethods;
    if (currentMethods.includes(method)) {
      newMethods = currentMethods.filter(m => m !== method);
    } else {
      newMethods = [...currentMethods, method];
    }

    // Update response with new evidence methods
    if (currentResponse.rating_level) {
      handleRating(currentResponse.rating_level, newMethods);
    } else {
      const newResponses = {
        ...responses,
        [criterion.id]: {
          ...currentResponse,
          evidence_methods_used: newMethods
        }
      };
      setResponses(newResponses);
    }
  };

  const handleFileUpload = (criterionId, files) => {
    // Store file references (in production, you'd upload to storage first)
    const fileList = Array.from(files);
    setUploadedFiles(prev => ({
      ...prev,
      [criterionId]: [...(prev[criterionId] || []), ...fileList]
    }));
  };

  const handleRemoveFile = (criterionId, fileIndex) => {
    setUploadedFiles(prev => ({
      ...prev,
      [criterionId]: prev[criterionId]?.filter((_, idx) => idx !== fileIndex) || []
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleNext = () => {
    if (currentIndex < criteria.length - 1) {
      setCurrentIndex(currentIndex + 1);
      scrollToTop();
      checkMilestone(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      scrollToTop();
    }
  };

  const scrollToTop = () => {
    if (questionScrollRef.current) {
      questionScrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const checkMilestone = (index) => {
    const progress = ((index + 1) / criteria.length) * 100;

    if (progress >= 25 && progress < 26) {
      showMilestone('25% Selesai!', 'Teruskan usaha yang baik.');
    } else if (progress >= 50 && progress < 51) {
      showMilestone('Setengah Jalan!', 'Anda sudah jauh.');
    } else if (progress >= 75 && progress < 76) {
      showMilestone('75% Selesai!', 'Hampir selesai.');
    }
  };

  const showMilestone = (message, subtitle) => {
    setCelebrationMessage(`${message}\n${subtitle}`);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 2000);
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      showDialog({
        type: 'warning',
        title: 'Belum Selesai',
        message: 'Harap nilai semua kriteria sebelum mengirim.',
        confirmText: 'Mengerti'
      });
      return;
    }

    setSubmitting(true);

    try {
      // Prepare PKKS responses
      const responsesArray = Object.values(responses).map(r => ({
        session_id: sessionId,
        criterion_id: r.criterion_id,
        rubric_id: r.rubric_id,
        user_id: profile?.id,
        rating_level: r.rating_level,
        rating_score: r.rating_score,
        evidence_methods_used: r.evidence_methods_used || []
      }));

      const { error: submitError } = await rubricService.submitPkksResponses(responsesArray);

      if (submitError) throw submitError;

      // Mark session as completed
      const { error: completeError } = await assessmentService.completeSession(sessionId);

      if (completeError) throw completeError;

      // Clear all saved data and reset state
      clearSavedData();
      localStorage.removeItem(storageKey);
      setResponses({});
      setCurrentIndex(0);
      setUploadedFiles({});
      setSessionId(null);

      // Show success and redirect
      showDialog({
        type: 'success',
        title: 'Penilaian Berhasil!',
        message: 'Terima kasih telah menyelesaikan penilaian PKKS!\n\nData penilaian telah tersimpan dengan baik.',
        confirmText: 'Kembali ke Dashboard',
        onConfirm: () => navigate('/dashboard', { replace: true })
      });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      showDialog({
        type: 'error',
        title: 'Gagal Mengirim',
        message: 'Terjadi kesalahan saat mengirim penilaian.\nSilakan coba lagi.',
        confirmText: 'OK'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndExit = () => {
    showDialog({
      type: 'confirm',
      title: 'Simpan & Keluar',
      message: 'Progress Anda telah disimpan secara otomatis.\n\nAnda dapat melanjutkan penilaian ini nanti dari dashboard Anda.',
      confirmText: 'Keluar',
      cancelText: 'Lanjutkan Penilaian',
      onConfirm: () => navigate('/dashboard')
    });
  };

  const canSubmit = () => {
    return criteria.every(c => responses[c.id]?.rating_level);
  };

  const getProgress = () => {
    const answered = Object.keys(responses).filter(id => responses[id]?.rating_level).length;
    return (answered / criteria.length) * 100;
  };

  const getAnsweredCount = () => {
    return Object.keys(responses).filter(id => responses[id]?.rating_level).length;
  };

  // Group criteria by competency area
  const getCompetencyAreaName = (criterion) => {
    if (!criterion) return 'Lainnya';
    return criterion.metadata?.competency_area_name || 'Lainnya';
  };

  const getRatingLevelDescription = (criterion, levelValue) => {
    if (!criterion) return '';
    const level = criterion.rating_levels?.find(l => l.level === levelValue);
    return level?.description || '';
  };

  // Handle school selection - defined before conditional returns
  const handleSchoolSelect = async (schoolId) => {
    try {
      const { school, error } = await schoolService.getSchool(schoolId);
      if (error) {
        console.error('Error fetching school:', error);
        showDialog({
          type: 'error',
          title: 'Gagal Memuat Sekolah',
          message: 'Terjadi kesalahan saat memuat data sekolah. Silakan coba lagi.',
          confirmText: 'OK'
        });
        return;
      }

      // Validate saved session belongs to the selected school
      if (savedData?.sessionId) {
        const { session } = await assessmentService.getSession(savedData.sessionId);
        const savedSchoolId = session?.target_school_id;
        if (savedSchoolId && String(savedSchoolId) !== String(schoolId)) {
          // Clear stale session data from different school
          clearSavedData();
          setResponses({});
          setSessionId(null);
          setCurrentIndex(0);
          setUploadedFiles({});
          rubricLoadedRef.current = false;
        }
      }

      setSelectedSchool(school);
      setSelectedSchoolId(schoolId);
      setShowSchoolSelector(false);
      setLoading(true);
    } catch (err) {
      console.error('Exception in handleSchoolSelect:', err);
      showDialog({
        type: 'error',
        title: 'Terjadi Kesalahan',
        message: 'Terjadi kesalahan tak terduga. Silakan coba lagi.',
        confirmText: 'OK'
      });
    }
  };

  // Show school selector modal FIRST (before loading check)
  if (showSchoolSelector) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Pilih Sekolah</h2>
            <p className="text-gray-600 mt-2">
              Pilih sekolah yang akan Anda nilai kepala sekolahnya
            </p>
          </div>

          <div className="mb-6">
            <SchoolSelector
              value={null}
              onChange={handleSchoolSelect}
              label="Sekolah yang Dinilai"
              placeholder="Pilih sekolah..."
              required={true}
            />
          </div>

          <div className="text-center text-sm text-gray-500">
            Anda akan melakukan penilaian PKKS untuk kepala sekolah yang dipilih
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat rubrik PKKS...</p>
        </div>
      </div>
    );
  }

  // Handle case when no criteria loaded
  if (!criteria || criteria.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <p className="text-gray-800 font-medium">Tidak dapat memuat kriteria penilaian</p>
          <p className="text-gray-600 text-sm mt-2">Pastikan data rubrik PKKS sudah di-seed ke database</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  const currentCriterion = criteria[currentIndex];
  const currentResponse = responses[currentCriterion?.id];
  const competencyArea = getCompetencyAreaName(currentCriterion);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      {/* School Info Banner */}
      {selectedSchool && (
        <div className="bg-purple-600 text-white py-2 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="font-medium">{selectedSchool.nama}</span>
              <span className="text-purple-200">| NPSN: {selectedSchool.npsn}</span>
            </div>
            <button
              onClick={() => {
                setResponses({});
                setSessionId(null);
                setCurrentIndex(0);
                setUploadedFiles({});
                clearSavedData();
                setShowSchoolSelector(true);
                setSelectedSchoolId(null);
                setSelectedSchool(null);
                rubricLoadedRef.current = false; // Reset to allow loading rubric for new school
              }}
              className="text-purple-200 hover:text-white text-sm underline"
            >
              Ganti Sekolah
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Penilaian PKKS
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {rubric?.rubric_name || 'Penilaian Kinerja Kepala Sekolah'}
              </p>
            </div>
            <button
              onClick={handleSaveAndExit}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Simpan & Keluar
            </button>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Progres: {getAnsweredCount()} dari {criteria.length} kriteria</span>
              <span>{Math.round(getProgress())}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-500"
                style={{ width: `${getProgress()}%` }}
              />
            </div>
          </div>

          {/* Save Status */}
          <div className="flex items-center justify-between mt-3 text-xs">
            <div className="flex items-center space-x-4">
              {isSaving && (
                <span className="text-purple-600 flex items-center">
                  <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Menyimpan...
                </span>
              )}
              {!isSaving && lastSaved && (
                <span className="text-green-600 flex items-center">
                  Progres tersimpan
                </span>
              )}
              {isSyncing && (
                <span className="text-indigo-600">Menyinkronkan ke server...</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Criterion Content */}
      <div className="max-w-4xl mx-auto px-4 py-8" ref={questionScrollRef}>
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
          {/* Competency Area Badge */}
          <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            {competencyArea}
          </div>

          {/* Criterion Number */}
          <div className="text-sm font-semibold text-purple-600 mb-2">
            Kriteria {currentCriterion?.criterion_code} ({currentIndex + 1} dari {criteria.length})
          </div>

          {/* Criterion Name */}
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            {currentCriterion?.criterion_name}
          </h2>

          {/* Criterion Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {currentCriterion?.description}
          </p>

          {/* Rating Level Options */}
          <div className="space-y-3 mb-6">
            <div className="text-sm font-medium text-gray-700 mb-2">Pilih Tingkat Pencapaian:</div>
            {ratingLevels.map((level) => (
              <button
                key={level.value}
                onClick={() => handleRating(level.value, currentResponse?.evidence_methods_used || [])}
                className={`
                  w-full p-4 rounded-lg border-2 text-left transition-all
                  ${currentResponse?.rating_level === level.value
                    ? `${level.selectedColor} shadow-md scale-[1.01] border-2`
                    : level.color
                  }
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{level.label}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {getRatingLevelDescription(currentCriterion, level.value)}
                    </p>
                  </div>
                  {currentResponse?.rating_level === level.value && (
                    <span className="text-purple-600 text-xl ml-2">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Evidence Methods */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <button
              onClick={() => setExpandedEvidence(prev => ({ ...prev, [currentCriterion?.id]: !prev[currentCriterion?.id] }))}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="text-sm font-medium text-gray-700">
                Bukti Pendukung (Opsional)
              </div>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${expandedEvidence[currentCriterion?.id] ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedEvidence[currentCriterion?.id] && (
              <div className="mt-3 space-y-4">
                {/* Evidence Method Checkboxes */}
                <div className="space-y-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Metode Pengumpulan Bukti</div>
                  {currentCriterion?.evidence_methods?.map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={currentResponse?.evidence_methods_used?.includes(method) || false}
                        onChange={() => handleEvidenceToggle(method)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">
                        {evidenceMethodLabels[method] || method}
                      </span>
                    </label>
                  ))}
                  {!currentCriterion?.evidence_methods?.length && (
                    <>
                      {['telaah_dokumen', 'observasi', 'wawancara'].map((method) => (
                        <label key={method} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={currentResponse?.evidence_methods_used?.includes(method) || false}
                            onChange={() => handleEvidenceToggle(method)}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">
                            {evidenceMethodLabels[method]}
                          </span>
                        </label>
                      ))}
                    </>
                  )}
                </div>

                {/* Document Upload Section */}
                <div className="border-t pt-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Unggah Dokumen Bukti</div>

                  {/* File Upload Input */}
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 hover:border-purple-400 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-sm text-gray-500">
                        <span className="font-medium text-purple-600">Klik untuk unggah</span> atau drag & drop
                      </p>
                      <p className="text-xs text-gray-400">PDF, DOC, XLS, JPG, PNG (Maks. 10MB)</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload(currentCriterion?.id, e.target.files)}
                    />
                  </label>

                  {/* Uploaded Files List */}
                  {uploadedFiles[currentCriterion?.id]?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {uploadedFiles[currentCriterion?.id].map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border text-sm">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <p className="text-gray-700 truncate max-w-xs">{file.name}</p>
                              <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveFile(currentCriterion?.id, idx)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`
                px-6 py-3 rounded-lg font-medium transition-all
                ${currentIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }
              `}
            >
              ← Sebelumnya
            </button>

            {currentIndex === criteria.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit() || submitting}
                className={`
                  px-8 py-3 rounded-lg font-semibold transition-all
                  ${canSubmit() && !submitting
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                {submitting ? 'Mengirim...' : 'Kirim Penilaian'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!currentResponse?.rating_level}
                className={`
                  px-6 py-3 rounded-lg font-medium transition-all
                  ${currentResponse?.rating_level
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                Selanjutnya →
              </button>
            )}
          </div>

          {/* Helper Text */}
          {!currentResponse?.rating_level && (
            <p className="text-center text-sm text-gray-500 mt-4">
              Silakan pilih tingkat pencapaian untuk melanjutkan
            </p>
          )}
        </div>
      </div>

      {/* Milestone Celebration */}
      {showCelebration && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center animate-bounce border-4 border-purple-500">
            <p className="text-3xl font-bold text-gray-900 whitespace-pre-line">
              {celebrationMessage}
            </p>
          </div>
        </div>
      )}

      {/* Dialog Modal */}
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText={dialog.confirmText}
        cancelText={dialog.cancelText}
        onConfirm={dialog.onConfirm}
      />
    </div>
  );
};

export default SupervisorAssessment;
